import {
  Button,
  Center,
  Flex,
  Heading,
  Icon,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
} from "@chakra-ui/react";
import { LockIcon, UserIcon } from "@icons";
import { useColor } from "@styles/useColor";
import { defaultDerivationPathTemplates } from "@umami/chains";
import { useDynamicModalContext, useMultiForm } from "@umami/components";
import { useHandleSession, useIsPasswordSet } from "@umami/state";
import { FormProvider } from "react-hook-form";

import { type FormFields, type Mode } from "./types";
import { ModalBackButton } from "../../BackButton";
import { ModalCloseButton } from "../../CloseButton";
import { PasswordInput } from "../../PasswordInput";
import { AdvancedAccountSettings } from "../AdvancedAccountSettings";
import { useGetSetupPasswordSubmitHandler } from "./useGetSetupPasswordSubmitHandler";

type SetupPasswordProps = {
  mode: Mode;
  onSubmit?: (password: string) => any;
};

const getModeConfig = (mode: Mode) => {
  switch (mode) {
    case "verification":
      return {
        icon: LockIcon,
        title: "Confirm password",
        buttonLabel: "Confirm",
        subtitle: "Confirm the password to secure your wallet and verification process.",
      };
    case "new_mnemonic":
      return {
        icon: UserIcon,
        title: "Create password",
        buttonLabel: "Create account",
        subtitle:
          "Set a password to unlock your wallet.\n Make sure to store your password safely.",
      };
    case "mnemonic":
    case "secret_key":
    case "import_backup":
      return {
        icon: LockIcon,
        title: "Almost there",
        buttonLabel: "Import wallet",
      };
    case "add_account":
      return {
        icon: LockIcon,
        title: "Confirm password",
        buttonLabel: "Add account",
        subtitle: "Confirm the password to add your account.",
      };
  }
};

export const DEFAULT_CURVE = "secp256k1";

export const SetupPassword = ({ mode, onSubmit }: SetupPasswordProps) => {
  const color = useColor();
  const { onSubmit: onGetSetupPasswordSubmit, isLoading } = useGetSetupPasswordSubmitHandler(mode);
  const isPasswordSet = useIsPasswordSet();
  const { setupSessionTimeout } = useHandleSession();
  const { allFormValues } = useDynamicModalContext();

  const form = useMultiForm<FormFields>({
    mode: "onBlur",
    defaultValues: {
      curve: allFormValues.current?.curve || DEFAULT_CURVE,
      derivationPaths: allFormValues.current?.derivationPaths || defaultDerivationPathTemplates,
    },
  });

  const {
    formState: { isValid },
    getValues,
  } = form;

  const { title, subtitle, buttonLabel, icon } = getModeConfig(mode);

  const handleSubmit = async (values: FormFields) => {
    if (onSubmit) {
      await onSubmit(values.password);
    } else {
      await onGetSetupPasswordSubmit(values);
    }

    if (mode === "new_mnemonic") {
      setupSessionTimeout();
    }
  };

  // show advanced settings (curve and derivation paths) only when importing a mnemonic account
  const isAdvancedAccountSettingsVisible = mode === "mnemonic";

  return (
    <ModalContent>
      <ModalHeader>
        <ModalBackButton />
        <ModalCloseButton />
        <Center flexDirection="column" gap="12px" whiteSpace="break-spaces">
          <Icon as={icon} width="24px" height="24px" color={color("400")} />
          <Heading marginTop="4px" size="xl">
            {title}
          </Heading>
          {subtitle && (
            <Text width="full" color={color("700")} fontWeight="400" textAlign="center" size="md">
              {subtitle}
            </Text>
          )}
        </Center>
      </ModalHeader>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <ModalBody>
            <Flex flexDirection="column" gap="24px">
              <PasswordInput
                inputName="password"
                isStrengthCheckEnabled={!isPasswordSet}
                label={isPasswordSet ? "Password" : "Set password"}
                required="Password is required"
              />
              {!isPasswordSet && (
                <PasswordInput
                  inputName="passwordConfirmation"
                  label="Confirm password"
                  required="Password confirmation is required"
                  validate={value => value === getValues("password") || "Passwords do not match"}
                />
              )}

              {isAdvancedAccountSettingsVisible && <AdvancedAccountSettings />}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button
              width="full"
              isDisabled={!isValid}
              isLoading={isLoading}
              type="submit"
              variant="primary"
            >
              {buttonLabel}
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </ModalContent>
  );
};
